import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Title, useDataProvider } from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ComparisonSankeyChart from "./ComparisonSankeyChart";
import ComparisonCveTable from "./ComparisonCveTable";
import type { ComparisonCve, ComparisonCveRow, ComparisonStatus } from "./ReleaseComparison";

interface PreReleaseComparisonData {
  preRelease: { id: string; name: string; totalFindings: number };
  release: { id: string; name: string; version: string; totalCves: number };
  comparison: {
    fixed: { label: string; count: number; cves: ComparisonCve[] };
    unfixed: { label: string; count: number; cves: ComparisonCve[] };
    new: { label: string; count: number; cves: ComparisonCve[] };
  };
}

const PreReleaseComparison: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dataProvider = useDataProvider() as any;

  const [releaseNames, setReleaseNames] = useState<string[]>([]);
  const [releaseVersions, setReleaseVersions] = useState<
    { id: string; version: string }[]
  >([]);
  const [releaseName, setReleaseName] = useState("");
  const [releaseId, setReleaseId] = useState("");

  const [comparisonData, setComparisonData] =
    useState<PreReleaseComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReleaseNames = async () => {
      try {
        const names = await dataProvider.getUnique("cve/releases", {
          field: "name",
        });
        setReleaseNames(names as string[]);
        if (names.length > 0) {
          setReleaseName(names[0]);
          await fetchVersions(names[0]);
        }
      } catch (err) {
        setError("Failed to load releases");
      }
    };
    fetchReleaseNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider]);

  const fetchVersions = async (name: string) => {
    try {
      const { data } = await dataProvider.getList("cve/releases", {
        filter: { name },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "version", order: "DESC" },
      });
      const versions = data.map((r: any) => ({ id: r.id, version: r.version }));
      setReleaseVersions(versions);
      if (versions.length > 0) {
        setReleaseId(versions[0].id);
      }
    } catch (err) {
      setError("Failed to load versions");
    }
  };

  const handleCompare = async () => {
    if (!id || !releaseId) {
      setError("Please select a release to compare against");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.comparePreReleaseToRelease(
        id,
        releaseId
      );
      if (!data?.preRelease) {
        throw new Error("Invalid response from comparison API");
      }
      setComparisonData(data as PreReleaseComparisonData);
    } catch (err) {
      console.error("Comparison error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to compare pre-release"
      );
    } finally {
      setLoading(false);
    }
  };

  const csvEscape = (value: string | number | null | undefined) => {
    const str = value === null || value === undefined ? "" : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const downloadCsv = (csv: string, filename: string) => {
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv)
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const CVE_CSV_HEADERS = [
    "name",
    "severity",
    "cvssScore",
    "imageRepository",
    "imageTag",
    "availableFixedImageBuildTag",
    "availableFixedReleaseVersion",
  ] as const;

  const exportComparisonCsv = () => {
    if (!comparisonData) return;
    const headers = ["comparisonStatus", ...CVE_CSV_HEADERS];
    const rows: Array<[ComparisonStatus, ComparisonCve]> = [
      ...comparisonData.comparison.fixed.cves.map(
        (c): [ComparisonStatus, ComparisonCve] => ["fixed", c]
      ),
      ...comparisonData.comparison.unfixed.cves.map(
        (c): [ComparisonStatus, ComparisonCve] => ["unfixed", c]
      ),
      ...comparisonData.comparison.new.cves.map(
        (c): [ComparisonStatus, ComparisonCve] => ["new", c]
      ),
    ];
    const csv = [headers.join(",")]
      .concat(
        rows.map(([status, c]) =>
          [
            csvEscape(status),
            ...CVE_CSV_HEADERS.map((header) => csvEscape(c[header])),
          ].join(",")
        )
      )
      .join("\n");
    downloadCsv(
      csv,
      `pre-release-comparison-${comparisonData.preRelease.name}-vs-${comparisonData.release.version}.csv`
    );
  };

  const comparisonRows: ComparisonCveRow[] = comparisonData
    ? [
        ...comparisonData.comparison.fixed.cves.map(
          (c): ComparisonCveRow => ({ ...c, comparisonStatus: "fixed" })
        ),
        ...comparisonData.comparison.unfixed.cves.map(
          (c): ComparisonCveRow => ({ ...c, comparisonStatus: "unfixed" })
        ),
        ...comparisonData.comparison.new.cves.map(
          (c): ComparisonCveRow => ({ ...c, comparisonStatus: "new" })
        ),
      ]
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/cve/pre-releases/${id}/show`)}
        >
          Back to Pre-Release
        </Button>
      </Box>
      <Title title="Compare Pre-Release to Release" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select a Release to Compare Against
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Select
              value={releaseName}
              onChange={(e) => {
                setReleaseName(e.target.value);
                fetchVersions(e.target.value);
              }}
              sx={{ width: "250px" }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Product
              </MenuItem>
              {releaseNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={releaseId}
              onChange={(e) => setReleaseId(e.target.value)}
              sx={{ width: "150px" }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Version
              </MenuItem>
              {releaseVersions.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.version}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              onClick={handleCompare}
              disabled={loading || !releaseId}
            >
              {loading ? "Comparing..." : "Compare"}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {loading && <CircularProgress />}
        </CardContent>
      </Card>

      {comparisonData && !loading && (
        <>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportComparisonCsv}
            >
              Export Comparison CSV
            </Button>
          </Box>
          <Box sx={{ mb: 3 }}>
            <ComparisonSankeyChart
              version1Label={`${comparisonData.release.name} ${comparisonData.release.version}`}
              version2Label={comparisonData.preRelease.name}
              fixedCount={comparisonData.comparison.fixed.count}
              unfixedCount={comparisonData.comparison.unfixed.count}
              newCount={comparisonData.comparison.new.count}
            />
          </Box>

          <ComparisonCveTable rows={comparisonRows} />
        </>
      )}
    </Box>
  );
};

export default PreReleaseComparison;
